import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { catchError, EMPTY, finalize, forkJoin, map, of, switchMap, tap } from 'rxjs';

import { ICampaignDetail, ICampaignWinner, IPromotionWinnerRow, PromotionsService } from 'core';
import { AvatarService } from 'core/services/avatar.service';
import { KorviuLoaderComponent, KorviuNoDataComponent } from 'shared';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { PromotionDetailWinnersModalComponent } from '../promotion-detail-winners-modal/promotion-detail-winners-modal.component';

interface ICampaignWinnerView extends ICampaignWinner {
  viewId: string;
  name: string;
  avatarUrl: string;
  medalUrl: string;
  rewardLabelKey: string;
}

interface ICampaignConditionView {
  viewId: string;
  actionLabelKey: string;
  minCount: number;
}

interface ICampaignWinnersPageMeta {
  totalPages: number;
  count: number;
}

const WINNERS_PAGE_SIZE = 10;
type CampaignDetailTab = 'details' | 'entries' | 'conditions';

@Component({
  selector: 'app-campaign-details',
  imports: [DatePipe, KorviuLoaderComponent, KorviuNoDataComponent, TranslatePipe],
  templateUrl: './campaign-details.component.html',
  styleUrl: './campaign-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CampaignDetailsComponent implements OnInit {
  readonly #avatarService = inject(AvatarService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #modalService = inject(NzModalService);
  readonly #promotionsService = inject(PromotionsService);
  readonly #route = inject(ActivatedRoute);
  readonly #translateService = inject(TranslateService);

  readonly tabs: { id: CampaignDetailTab; labelKey: string }[] = [
    { id: 'details', labelKey: 'promotions.detail.tabs.details' },
    { id: 'entries', labelKey: 'promotions.detail.tabs.entries' },
    { id: 'conditions', labelKey: 'promotions.detail.tabs.conditions' }
  ];
  readonly ticketSlots = [1, 2, 3, 4, 5, 6, 7] as const;
  readonly campaignId = signal<number | null>(null);
  readonly campaign = signal<ICampaignDetail | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly loadError = signal<boolean>(false);
  readonly winners = signal<ICampaignWinner[]>([]);
  readonly winnersIsLoading = signal<boolean>(false);
  readonly winnersLoadError = signal<boolean>(false);
  readonly winnersCurrentPage = signal<number>(1);
  readonly winnersPageMeta = signal<ICampaignWinnersPageMeta>({ totalPages: 1, count: 0 });
  readonly selectedTab = signal<CampaignDetailTab>('details');
  readonly currentLang = signal<string>(
    this.#translateService.currentLang || this.#translateService.defaultLang || 'es'
  );
  readonly isEnded = computed(() => {
    const status = this.campaign()?.status;

    return status === 'expired' || status === 'archived';
  });
  readonly isWinner = computed(() => this.campaign()?.participant_status === 'winner');
  readonly filledTicketCount = computed(() => {
    return Math.min(this.ticketSlots.length, this.campaign()?.user_progress.entries_count ?? 0);
  });
  readonly objectivePercent = computed(() => {
    const objectiveProgress = this.campaign()?.objective_progress;

    if (!objectiveProgress?.target) {
      return 0;
    }

    return Math.min(100, Math.max(0, (objectiveProgress.current / objectiveProgress.target) * 100));
  });
  readonly entriesChanceKey = computed(() => {
    return this.isEnded()
      ? 'promotions.campaignDetails.entriesChanceEnded'
      : 'promotions.campaignDetails.entriesChanceActive';
  });
  readonly entriesChanceParams = computed(() => {
    const campaign = this.campaign();

    if (!campaign) {
      return { count: 0, total: 0 };
    }

    const count = campaign.user_progress.entries_count;
    const total = campaign.caps.total_entry_cap ?? campaign.user_progress.max_entries_per_user ?? count;

    return { count, total };
  });
  readonly stateBannerTitleKey = computed(() => {
    if (this.isWinner()) {
      return 'promotions.campaignDetails.stateBanner.wonTitle';
    }

    if (this.isEnded()) {
      return 'promotions.campaignDetails.stateBanner.lostTitle';
    }

    if (this.campaign()?.participant_status === 'active') {
      return 'promotions.campaignDetails.stateBanner.participatingTitle';
    }

    return 'promotions.campaignDetails.stateBanner.activeTitle';
  });
  readonly stateBannerBodyKey = computed(() => {
    if (this.isWinner()) {
      return 'promotions.campaignDetails.stateBanner.wonBody';
    }

    if (this.isEnded()) {
      return 'promotions.campaignDetails.stateBanner.lostBody';
    }

    if (this.campaign()?.participant_status === 'active') {
      return 'promotions.campaignDetails.stateBanner.participatingBody';
    }

    return 'promotions.campaignDetails.stateBanner.activeBody';
  });
  readonly statusLabelKey = computed(() => {
    const status = this.campaign()?.status;

    if (status === 'expired' || status === 'archived') {
      return 'promotions.detail.statusTags.ended';
    }

    if (status === 'active') {
      return 'promotions.detail.statusTags.active';
    }

    return `promotions.campaignDetails.statuses.${status ?? 'draft'}`;
  });
  readonly entryCost = computed(() => this.campaign()?.caps.entry_cost?.trim() ?? '');
  readonly costIsFree = computed(() => !this.entryCost() || this.entryCost().toLowerCase() === 'free');
  readonly ganasBadgeValue = computed(() => {
    const reward = this.campaign()?.rewards.find(item => item.reward_type === 'ganas');

    if (!reward?.value) {
      return null;
    }

    const numericValue = Number(reward.value);

    return Number.isFinite(numericValue)
      ? new Intl.NumberFormat(this.currentLang(), { maximumFractionDigits: 0 }).format(numericValue)
      : reward.value;
  });
  readonly winnerRows = computed<ICampaignWinnerView[]>(() => {
    return this.winners().map((winner, index) => ({
      ...winner,
      viewId: `${winner.reward.rank ?? 'unranked'}-${winner.claimed_at}-${index}`,
      name: [winner.participant.first_name, winner.participant.last_initial].filter(Boolean).join(' '),
      avatarUrl: winner.participant.avatar
        ? this.#avatarService.getUserAvatar(winner.participant.avatar)
        : 'assets/img/promotions/winner-avatar.png',
      medalUrl: this.#getMedalUrl(winner.reward.rank),
      rewardLabelKey: `promotions.campaignDetails.rewardTypes.${winner.reward.reward_type}`
    }));
  });
  readonly topWinnerRows = computed<ICampaignWinnerView[]>(() => this.winnerRows().slice(0, 3));
  readonly conditionRows = computed<ICampaignConditionView[]>(() => {
    return (this.campaign()?.conditions ?? []).map((condition, index) => ({
      viewId: `${condition.mode ?? 'unknown'}-${condition.action ?? 'unknown'}-${condition.min_count}-${index}`,
      actionLabelKey: condition.action
        ? `promotions.campaignDetails.objectives.${condition.action}`
        : 'promotions.campaignDetails.objective',
      minCount: condition.min_count ?? 1
    }));
  });

  public ngOnInit(): void {
    this.#translateService.onLangChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(event => this.currentLang.set(event.lang));

    this.#route.paramMap
      .pipe(
        map(params => Number(params.get('campaignId'))),
        switchMap(campaignId => {
          if (!Number.isInteger(campaignId) || campaignId <= 0) {
            this.loadError.set(true);
            return EMPTY;
          }

          this.campaignId.set(campaignId);
          this.isLoading.set(true);
          this.loadError.set(false);
          this.winnersIsLoading.set(true);
          this.winnersLoadError.set(false);

          return forkJoin({
            campaign: this.#promotionsService.getCampaignDetail(campaignId),
            winners: this.#promotionsService
              .getCampaignWinners(campaignId, { page: 1, pageSize: WINNERS_PAGE_SIZE })
              .pipe(
                catchError(() => {
                  this.winnersLoadError.set(true);
                  return of(this.#emptyWinnersResponse());
                })
              )
          }).pipe(
            catchError(() => {
              this.loadError.set(true);
              return EMPTY;
            }),
            finalize(() => {
              this.isLoading.set(false);
              this.winnersIsLoading.set(false);
            })
          );
        }),
        tap(({ campaign, winners }) => {
          this.campaign.set(campaign);
          this.#setWinnersResponse(winners, 1);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public retry(): void {
    const campaignId = this.campaignId();

    if (!campaignId) {
      return;
    }

    this.isLoading.set(true);
    this.loadError.set(false);
    this.winnersIsLoading.set(true);
    this.winnersLoadError.set(false);

    forkJoin({
      campaign: this.#promotionsService.getCampaignDetail(campaignId),
      winners: this.#promotionsService.getCampaignWinners(campaignId, { page: 1, pageSize: WINNERS_PAGE_SIZE }).pipe(
        catchError(() => {
          this.winnersLoadError.set(true);
          return of(this.#emptyWinnersResponse());
        })
      )
    })
      .pipe(
        tap(({ campaign, winners }) => {
          this.campaign.set(campaign);
          this.#setWinnersResponse(winners, 1);
        }),
        catchError(() => {
          this.loadError.set(true);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
          this.winnersIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public onTabClick(tab: CampaignDetailTab): void {
    this.selectedTab.set(tab);
  }

  public openWinnersModal(): void {
    const campaign = this.campaign();
    const modalWinnerRows: IPromotionWinnerRow[] = this.winnerRows().map((winner, index) => {
      const rewardType = this.#translateService.instant(winner.rewardLabelKey);

      return {
        id: winner.viewId,
        place: winner.reward.rank ?? index + 1,
        name: winner.name,
        reward: winner.reward.value ? `${winner.reward.value} ${rewardType}` : rewardType
      };
    });

    if (!campaign || !modalWinnerRows.length) {
      return;
    }

    this.#modalService.create({
      nzContent: PromotionDetailWinnersModalComponent,
      nzData: {
        promotionName: campaign.name,
        winners: modalWinnerRows
      },
      nzCentered: true,
      nzClosable: false,
      nzFooter: null,
      nzWidth: 'auto',
      nzBodyStyle: { padding: '0', background: 'transparent' },
      nzWrapClassName: 'promotion-modal-wrap',
      nzClassName: 'promotion-winners-modal-dialog'
    });
  }

  public loadWinners(page: number): void {
    const campaignId = this.campaignId();
    const totalPages = this.winnersPageMeta().totalPages;

    if (!campaignId || page < 1 || page > totalPages) {
      return;
    }

    this.winnersIsLoading.set(true);
    this.winnersLoadError.set(false);

    this.#promotionsService
      .getCampaignWinners(campaignId, { page, pageSize: WINNERS_PAGE_SIZE })
      .pipe(
        tap(response => {
          this.#setWinnersResponse(response, page);
        }),
        catchError(() => {
          this.winnersLoadError.set(true);
          return EMPTY;
        }),
        finalize(() => {
          this.winnersIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #setWinnersResponse(response: IGetPaginatedResponse<ICampaignWinner[]>, page: number): void {
    this.winners.set(response.results);
    this.winnersCurrentPage.set(page);
    this.winnersPageMeta.set({
      totalPages: Math.max(1, response.total_pages),
      count: response.count
    });
  }

  #emptyWinnersResponse(): IGetPaginatedResponse<ICampaignWinner[]> {
    return {
      links: { next: null, previous: null },
      count: 0,
      total_pages: 1,
      per_page: WINNERS_PAGE_SIZE,
      results: []
    };
  }

  #getMedalUrl(rank: number | null): string {
    switch (rank) {
      case 2:
        return 'assets/img/promotions/winner-medal-silver.svg';
      case 3:
        return 'assets/img/promotions/winner-medal-bronze.svg';
      case 1:
      default:
        return 'assets/img/promotions/winner-medal-gold.svg';
    }
  }
}
